import { useCallback, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
    UIProvider,
    type UIComponentDefaults,
} from '@vnetwork/datacentral-ui/provider'
import RouterLink from '@/components/RouterLink'
import { translateFormError } from '@/utils/form-errors'

/**
 * Control defaults matching the previous form kit: number fields take any
 * decimals and negatives, use en-US separators and emit 0 when emptied;
 * selects are clearable.
 */
const DEFAULTS: UIComponentDefaults = {
    // en-US separators (1,500,000.5) in every UI language, like the old
    // NumericFormat thousandSeparator=",".
    numberInput: {
        precision: 10,
        allowNegative: true,
        emptyValue: 0,
        locale: 'en-US',
    },
    selectClearable: true,
}

/**
 * @vnetwork/datacentral-ui provider wired to the app: router links, the active i18n
 * language for built-in strings, and validation messages that are i18n keys.
 */
export function AppUIProvider({ children }: { children: ReactNode }) {
    const { t, i18n } = useTranslation()
    const locale = i18n.language?.startsWith('en') ? 'en' : 'vi'
    const translateError = useCallback(
        (message: string) => translateFormError(message, t),
        [t]
    )
    return (
        <UIProvider
            defaultTheme="light"
            linkComponent={RouterLink}
            locale={locale}
            translateError={translateError}
            defaults={DEFAULTS}
        >
            {children}
        </UIProvider>
    )
}
